import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  Group,
  ThemeIcon,
  Badge,
  Stack,
  Card,
  Skeleton,
  SimpleGrid,
  Anchor,
} from '@mantine/core';
import {
  IconTruck,
  IconBuilding,
  IconPackage,
  IconAlertTriangle,
  IconClock,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { PipelineEntry } from '../../lib/api/domains/procurement';
import dayjs from 'dayjs';

function ReadyCard({ entry }: { entry: PipelineEntry }) {
  const navigate = useNavigate();

  return (
    <Card
      withBorder
      padding="sm"
      style={{ cursor: 'pointer', borderColor: entry.isOverdue ? 'var(--mantine-color-red-6)' : undefined }}
      onClick={() => navigate(`/procurement/${entry.id}`)}
    >
      <Group justify="space-between" wrap="nowrap" mb={4}>
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon size="sm" color={entry.isOverdue ? 'red' : 'teal'} variant="light" radius="xl">
            <IconTruck size={13} />
          </ThemeIcon>
          <Text size="sm" fw={600} lineClamp={1}>
            {entry.customerName ?? 'Kein Kundenname'}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          {entry.isOverdue && (
            <Badge size="xs" color="red" leftSection={<IconAlertTriangle size={9} />}>
              Überfällig
            </Badge>
          )}
          <Badge size="xs" color="teal" variant="light">
            READY
          </Badge>
        </Group>
      </Group>

      <Group gap={12} mt={4}>
        {entry.weclappTicketNumber && (
          <Group gap={4}>
            <IconClock size={11} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">#{entry.weclappTicketNumber}</Text>
          </Group>
        )}
        <Group gap={4}>
          <IconPackage size={11} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">
            {entry.daysInStage}d in Stage
          </Text>
        </Group>
        {entry.expectedDeliveryAt && (
          <Group gap={4}>
            <IconBuilding size={11} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              bis {dayjs(entry.expectedDeliveryAt).format('DD.MM.')}
            </Text>
          </Group>
        )}
      </Group>
    </Card>
  );
}

export function ReadyDeliveriesWidget() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-ready'],
    queryFn: () => api.getPipelineEntries({ stage: 'READY', limit: 6 }),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;

  if (!isLoading && entries.length === 0) return null;

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" color="teal" variant="light" radius="xl">
            <IconTruck size={14} />
          </ThemeIcon>
          <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
            Lieferbereit{total > 0 ? ` (${total})` : ''}
          </Text>
        </Group>
        {total > 6 && (
          <Anchor size="xs" c="dimmed" onClick={() => navigate('/procurement')}>
            Alle anzeigen →
          </Anchor>
        )}
      </Group>

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
          {[1, 2].map((i) => <Skeleton key={i} h={80} radius="md" />)}
        </SimpleGrid>
      ) : (
        <Stack gap="sm">
          {entries.map((entry) => (
            <ReadyCard key={entry.id} entry={entry} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
